import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArticlesApi } from '@services/api/articles'
import { Article } from 'types/article'
import { useWindowDimensions } from '@hooks/use-window-dimensions'

interface LatestNewsSectionProps {
  className?: string
  articlesCount?: number
  showTitle?: boolean
  titleText?: string
  linkText?: string
  linkHref?: string
  initialArticles?: Article[]
}

export const LatestNewsSection: React.FC<LatestNewsSectionProps> = ({
  className = '',
  articlesCount = 12,
  showTitle = true,
  titleText = 'Últimas Notícias',
  linkText = 'Ver mais',
  linkHref = '/noticias',
  initialArticles
}) => {
  const [articles, setArticles] = useState<Article[]>(initialArticles || [])
  const [loading, setLoading] = useState(!initialArticles)
  const { width } = useWindowDimensions()

  useEffect(() => {
    // Se já temos artigos iniciais, não buscar novamente
    if (initialArticles && initialArticles.length > 0) {
      return
    }

    const fetchArticles = async () => {
      try {
        setLoading(true)
        const {
          data: { articles: fetchedArticles }
        } = await ArticlesApi.index({ per: articlesCount })

        setArticles(fetchedArticles)
      } catch (error) {
        setArticles([])
      } finally {
        setLoading(false)
      }
    }

    fetchArticles()
  }, [articlesCount, initialArticles])

  if (loading) {
    return (
      <section className={`container mx-5 px-4 py-8 ${className}`}>
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Carregando notícias...</p>
        </div>
      </section>
    )
  }

  if (!articles || articles.length === 0) {
    return null
  }

  return (
    <section className={`container mx-5 px-4 py-8 ${className}`}>
      {showTitle && (
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold">{titleText}</h2>
          <Link
            href={linkHref}
            className="hover:bg-gray-100 rounded-full border px-2 py-1 text-gray-400"
          >
            {linkText}
          </Link>
        </div>
      )}

      {/* Card destacado principal */}
      <a
        href={articles[0].path}
        className="cursor-pointer font-noto w-full h-full flex flex-col"
      >
        <div className="flex flex-col md:flex-row gap-2 md:gap-8 mb-12">
          <div key={articles[0].id} className="flex-1 max-w-2xl">
            <div className="relative h-96 md:h-[520px] w-full rounded-lg overflow-hidden">
              <Image
                src={
                  articles[0].thumbnailImage?.path ||
                  '/assets/default-article.jpg'
                }
                alt={articles[0].thumbnailImage?.alt || articles[0].title}
                fill
                className="object-cover"
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center relative">
            <div className="mb-4">
              <span className="bg-green text-white px-3 py-1 rounded text-sm font-medium mr-2">
                {articles[0].editorial?.name || 'NOTÍCIA'}
              </span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {articles[0].title}
            </h2>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed line-clamp-4">
              {articles[0].shortContent || 'Conteúdo da notícia em destaque.'}
            </p>
            <p className="text-gray-400 text-sm flex justify-between w-full absolute bottom-0">
              <span>
                {new Date(articles[0].publishedAt).toLocaleDateString('pt-BR')}
              </span>
              <span>{articles[0].timeToRead || 4} min de leitura</span>
            </p>
          </div>
        </div>
      </a>

      {/* Grid de artigos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {articles.slice(1, width > 600 ? 9 : 5).map(article => (
          <a
            key={article.id}
            href={article.path}
            className="cursor-pointer font-noto w-full h-full flex flex-col"
          >
            {/* shadow-md  */}
            <div className="bg-white  overflow-hidden h-96">
              <div className="relative h-48">
                <Image
                  src={
                    article.thumbnailImage?.path ||
                    '/assets/default-article.jpg'
                  }
                  alt={article.thumbnailImage?.alt || article.title}
                  fill
                  className="object-cover rounded-lg"
                />
              </div>
              {/* p-4  */}
              <div className="pt-2 pb-4 relative h-48">
                <span className="bg-green text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                  {article.editorial?.name || 'NOTÍCIA'}
                </span>
                <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                  {article.title}
                </h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-3">
                  {article.shortContent || 'Conteúdo da notícia em destaque.'}
                </p>
                <p className="text-xs text-gray-500 flex justify-between w-full absolute bottom-1">
                  <span>
                    {new Date(article.publishedAt).toLocaleDateString('pt-BR')}
                  </span>
                  <span className="pe-8">
                    {article.timeToRead} min de leitura
                  </span>
                </p>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  )
}
